import { Network, Workflow, TrendingUp, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for scaling / nationwide dispatch operations (guide Step/Mission 12).
export default function DispatchScalingExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="dispatch-scaling-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Network className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">Scaling with systems, not hustle</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Growth breaks businesses that don't have systems. Adding trucks and lanes without structure is how carriers grow
        straight into bankruptcy. Scaling is really about <strong>repeatable processes</strong>.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Workflow className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What "dispatch operations" means</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            The repeatable back-office engine — how loads get booked, drivers assigned, paperwork/BOLs collected,
            invoices sent and compliance tracked — that runs the same way every day, whether you have 1 truck or 10.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What breaks when you scale</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Compliance slips (a missed MVR or med card across more drivers), cash flow strains, and one bad hire's CSA
            spike can ground the whole fleet. Systems plus monitoring keep growth from breaking you.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          Scaling on hustle alone eventually collapses under its own weight. The carriers that survive growth are the
          ones that <strong>systematized dispatch, billing and compliance</strong> before they added the next truck.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Systematize one workflow at a time (booking → paperwork → billing), build direct shipper relationships, and
          watch your CSA scores as you add equipment. Delegate ops so you can focus on growth. This isn't business
          advice.
        </p>
      </div>
    </div>
  );
}
